<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { format } from 'date-fns';

import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import AddDeviceModal from './AddDeviceModal.vue';
import { useAlert } from 'dashboard/composables';
import callDevicesAPI from 'dashboard/api/callDevices';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useModuleGate } from 'dashboard/composables/useModuleGate';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

// Sem o módulo de ligações a tela é bloqueada pelo paywall (fechar volta).
useModuleGate('calling');

// ── state ─────────────────────────────────────────────────────────────────────
const devices = ref([]);
const plan = ref(null);
const isFetching = ref(false);
const searchQuery = ref('');
const activeTabIndex = ref(0);
const addModalRef = ref(null);
const loadingActions = ref({}); // {[id]: 'logout' | 'delete' | 'assign'}

// ── inboxes from Vuex ─────────────────────────────────────────────────────────
const inboxes = useMapGetter('inboxes/getInboxes');

// ── data fetching ─────────────────────────────────────────────────────────────

const fetchDevices = async () => {
  isFetching.value = true;
  try {
    const { data } = await callDevicesAPI.index();
    devices.value = Array.isArray(data) ? data : [];
  } catch {
    devices.value = [];
  } finally {
    isFetching.value = false;
  }
};

const fetchPlan = async () => {
  try {
    const { data } = await callDevicesAPI.callingPlan();
    plan.value = data;
  } catch {
    plan.value = null; // 402 (sem módulo) ou erro transitório: o paywall já cobre
  }
};

onBeforeMount(() => {
  fetchDevices();
  fetchPlan();
  if (!inboxes.value?.length) {
    store.dispatch('inboxes/get');
  }
});

// ── status helpers ─────────────────────────────────────────────────────────────

/**
 * Map backend state → display category
 *   'open' (=connected+paired) → 'connected'
 *   'connecting'|'qr' (pairing in progress) → 'pairing'
 *   'logged_out'|'unknown' → 'disconnected'
 */
const deviceStatus = device => {
  if (device.state === 'open' && device.paired) return 'connected';
  if (device.state === 'connecting' || device.state === 'qr') return 'pairing';
  return 'disconnected';
};

const isConnected = d => deviceStatus(d) === 'connected';
const isDisconnected = d => deviceStatus(d) === 'disconnected';

// ── filter counts ──────────────────────────────────────────────────────────────

const connectedCount = computed(() => devices.value.filter(isConnected).length);
const disconnectedCount = computed(
  () => devices.value.filter(isDisconnected).length
);

const tabs = computed(() => [
  { label: t('CALL_DEVICES.FILTER.ALL') },
  { label: t('CALL_DEVICES.FILTER.CONNECTED'), count: connectedCount.value },
  {
    label: t('CALL_DEVICES.FILTER.DISCONNECTED'),
    count: disconnectedCount.value,
  },
]);

// ── filtered list ──────────────────────────────────────────────────────────────

const filteredDevices = computed(() => {
  let list = devices.value;

  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter(d => d.name?.toLowerCase().includes(q));
  }

  if (activeTabIndex.value === 1) list = list.filter(isConnected);
  else if (activeTabIndex.value === 2) list = list.filter(isDisconnected);

  return list;
});

// ── actions ────────────────────────────────────────────────────────────────────

const handleLogout = async device => {
  loadingActions.value[device.id] = 'logout';
  try {
    await callDevicesAPI.logout(device.id);
    useAlert(t('CALL_DEVICES.TOAST.LOGOUT_SUCCESS'));
    await fetchDevices();
  } catch {
    useAlert(t('CALL_DEVICES.TOAST.LOGOUT_ERROR'));
  } finally {
    delete loadingActions.value[device.id];
  }
};

const handleDelete = async device => {
  loadingActions.value[device.id] = 'delete';
  try {
    await callDevicesAPI.destroy(device.id);
    useAlert(t('CALL_DEVICES.TOAST.DELETE_SUCCESS'));
    devices.value = devices.value.filter(d => d.id !== device.id);
  } catch {
    useAlert(t('CALL_DEVICES.TOAST.DELETE_ERROR'));
  } finally {
    delete loadingActions.value[device.id];
  }
};

const handleRePair = async device => {
  loadingActions.value[device.id] = 'pair';
  try {
    // A previously-paired device still holds its old WhatsApp credentials, so
    // WaCalls tries to RECONNECT (state stays "connecting") and never emits a
    // QR. Log it out first to drop the stale session, then pair() yields a
    // fresh QR. Open the modal straight into the QR step so the user can scan.
    await callDevicesAPI.logout(device.id);
    const { data } = await callDevicesAPI.pair(device.id);
    addModalRef.value?.openForDevice(device.id, data?.qr || '');
  } catch {
    useAlert(t('CALL_DEVICES.TOAST.PAIR_ERROR'));
  } finally {
    delete loadingActions.value[device.id];
  }
};

const openAddModal = () => {
  addModalRef.value?.open();
};

const onDeviceAdded = () => {
  fetchDevices();
};

// ── inbox assignment ───────────────────────────────────────────────────────────

const handleAssignInbox = async (device, inboxId) => {
  loadingActions.value[device.id] = 'assign';
  try {
    const payload = { inbox_id: inboxId || null };
    const { data } = await callDevicesAPI.update(device.id, payload);
    // update local row in-place
    const idx = devices.value.findIndex(d => d.id === device.id);
    if (idx !== -1) devices.value[idx] = { ...devices.value[idx], ...data };
    useAlert(t('CALL_DEVICES.INBOX.ASSIGN_SUCCESS'));
  } catch {
    useAlert(t('CALL_DEVICES.INBOX.ASSIGN_ERROR'));
  } finally {
    delete loadingActions.value[device.id];
  }
};

// ── date formatting ────────────────────────────────────────────────────────────

const formatDate = dateStr => {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
};

// ── phone number display ───────────────────────────────────────────────────────

const formatJid = jid => {
  if (!jid) return '';
  // jid format: "5511999999999@s.whatsapp.net" → "+5511999999999"
  const number = jid.split('@')[0];
  return `+${number}`;
};

// ── navigation ─────────────────────────────────────────────────────────────────

const goToDashboard = device => {
  router.push({
    name: 'call_device_dashboard',
    params: { ...route.params, deviceId: device.id },
  });
};
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="$t('CALL_DEVICES.TITLE')"
    :no-records-found="false"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CALL_DEVICES.TITLE')"
        :description="$t('CALL_DEVICES.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-plus"
            :label="$t('CALL_DEVICES.ADD_BUTTON')"
            @click="openAddModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <p v-if="plan" class="text-xs text-n-slate-11 mb-3">
        {{
          t('CALL_DEVICES.USAGE', {
            used: plan.devices_used,
            limit: plan.max_devices,
          })
        }}
      </p>

      <!-- search + filter row -->
      <div
        class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4"
      >
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="$t('CALL_DEVICES.SEARCH_PLACEHOLDER')"
          class="w-full sm:max-w-xs h-9 px-3 text-sm rounded-lg bg-n-alpha-1 border-0 outline outline-1 outline-n-weak focus:outline-n-brand text-n-slate-12 placeholder:text-n-slate-9 transition-colors"
        />
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTabIndex"
          @tab-changed="tab => (activeTabIndex = tabs.indexOf(tab))"
        />
      </div>

      <!-- table -->
      <div class="rounded-xl border border-n-weak overflow-hidden">
        <table class="min-w-full divide-y divide-n-weak">
          <thead class="bg-n-alpha-1">
            <tr>
              <th
                class="py-3 px-4 text-left text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
              >
                {{ $t('CALL_DEVICES.TABLE.NAME') }}
              </th>
              <th
                class="py-3 px-4 text-left text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
              >
                {{ $t('CALL_DEVICES.TABLE.STATUS') }}
              </th>
              <th
                class="py-3 px-4 text-left text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
              >
                {{ $t('CALL_DEVICES.TABLE.TOTAL_CALLS') }}
              </th>
              <th
                class="py-3 px-4 text-left text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
              >
                {{ $t('CALL_DEVICES.TABLE.INBOX') }}
              </th>
              <th
                class="py-3 px-4 text-left text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
              >
                {{ $t('CALL_DEVICES.TABLE.CREATED_AT') }}
              </th>
              <th class="py-3 px-4" />
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="device in filteredDevices"
              :key="device.id"
              class="hover:bg-n-alpha-1 transition-colors"
            >
              <!-- Name — click to open per-device dashboard -->
              <td class="py-3 px-4">
                <button
                  type="button"
                  class="text-left group"
                  @click="goToDashboard(device)"
                >
                  <span
                    class="font-medium text-sm text-n-slate-12 group-hover:text-n-brand transition-colors"
                  >
                    {{ device.name }}
                  </span>
                  <span
                    v-if="device.jid"
                    class="block text-xs text-n-slate-10 mt-0.5"
                  >
                    {{ formatJid(device.jid) }}
                  </span>
                </button>
              </td>

              <!-- Status -->
              <td class="py-3 px-4">
                <template v-if="deviceStatus(device) === 'connected'">
                  <div class="flex items-center gap-1.5">
                    <span class="i-lucide-circle-check size-4 text-n-teal-9" />
                    <span class="text-sm text-n-teal-9 font-medium">
                      {{ $t('CALL_DEVICES.STATUS.CONNECTED') }}
                    </span>
                  </div>
                </template>
                <template v-else-if="deviceStatus(device) === 'pairing'">
                  <div class="flex items-center gap-1.5">
                    <span
                      class="i-lucide-loader-2 size-4 text-n-amber-9 animate-spin"
                    />
                    <span class="text-sm text-n-amber-9 font-medium">
                      {{ $t('CALL_DEVICES.STATUS.PAIRING') }}
                    </span>
                  </div>
                </template>
                <template v-else>
                  <div class="flex items-center gap-1.5">
                    <span
                      class="w-2 h-2 rounded-full bg-n-slate-7 flex-shrink-0"
                    />
                    <span class="text-sm text-n-slate-10">
                      {{ $t('CALL_DEVICES.STATUS.DISCONNECTED') }}
                    </span>
                  </div>
                </template>
              </td>

              <!-- Total calls — not provided by backend yet; always 0 -->
              <td class="py-3 px-4">
                <div class="flex items-center gap-1.5 text-n-slate-11">
                  <span class="i-lucide-phone size-3.5" />
                  <!-- device.total_calls not yet returned by API; default to 0 -->
                  <span class="text-sm">{{ device.total_calls ?? 0 }}</span>
                </div>
              </td>

              <!-- Inbox assignment -->
              <td class="py-3 px-4">
                <select
                  :value="device.inbox_id ?? ''"
                  :disabled="loadingActions[device.id] === 'assign'"
                  class="h-8 px-2 text-sm rounded-lg bg-n-alpha-1 border-0 outline outline-1 outline-n-weak focus:outline-n-brand text-n-slate-12 disabled:opacity-50 transition-colors"
                  @change="
                    handleAssignInbox(device, $event.target.value || null)
                  "
                >
                  <option value="">{{ $t('CALL_DEVICES.INBOX.NONE') }}</option>
                  <option
                    v-for="inbox in inboxes"
                    :key="inbox.id"
                    :value="inbox.id"
                  >
                    {{ inbox.name }}
                  </option>
                </select>
              </td>

              <!-- Created at -->
              <td class="py-3 px-4 text-sm text-n-slate-11">
                {{ formatDate(device.created_at) }}
              </td>

              <!-- Actions -->
              <td class="py-3 px-4">
                <div class="flex items-center justify-end gap-1">
                  <Button
                    v-tooltip.top="$t('CALL_DEVICES.ACTIONS.RE_PAIR')"
                    icon="i-lucide-qr-code"
                    xs
                    slate
                    faded
                    :is-loading="loadingActions[device.id] === 'pair'"
                    @click="handleRePair(device)"
                  />
                  <Button
                    v-if="isConnected(device)"
                    v-tooltip.top="$t('CALL_DEVICES.ACTIONS.LOGOUT')"
                    icon="i-lucide-log-out"
                    xs
                    amber
                    faded
                    :is-loading="loadingActions[device.id] === 'logout'"
                    @click="handleLogout(device)"
                  />
                  <Button
                    v-tooltip.top="$t('CALL_DEVICES.ACTIONS.DELETE')"
                    icon="i-lucide-trash-2"
                    xs
                    ruby
                    faded
                    :is-loading="loadingActions[device.id] === 'delete'"
                    @click="handleDelete(device)"
                  />
                </div>
              </td>
            </tr>

            <!-- empty state inside table -->
            <tr v-if="!isFetching && filteredDevices.length === 0">
              <td colspan="6" class="py-16 text-center text-sm text-n-slate-10">
                {{ $t('CALL_DEVICES.EMPTY') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- footer count -->
      <p class="mt-3 text-xs text-n-slate-10">
        {{ $t('CALL_DEVICES.FOOTER', { count: filteredDevices.length }) }}
      </p>
    </template>
  </SettingsLayout>

  <!-- Add / QR pairing modal -->
  <AddDeviceModal
    ref="addModalRef"
    @close="() => {}"
    @device-added="onDeviceAdded"
  />
</template>
